import { Alert, Button, Flex } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React from "react"
import { useTranslation } from "react-i18next"

import { type LeadMissingField } from "./lead-missing-fields.ts"

interface LeadStageRetryBannerProps {
  targetStatus: number | null
  statuses: BaseOptionType[]
  fields: LeadMissingField[]
  saving?: boolean
  onRetry: () => void
  onDismiss: () => void
}

/**
 * The stage a rejected transition was aiming for, kept in front of the user
 * until it lands. It is the workspace's answer to the edit form preselecting the
 * target stage in its Lifecycle section.
 */
function LeadStageRetryBanner({
  targetStatus,
  statuses,
  fields,
  saving,
  onRetry,
  onDismiss
}: LeadStageRetryBannerProps) {
  const { t } = useTranslation()

  if (targetStatus == null) return null

  const stage =
    statuses.find((option) => String(option?.value) === String(targetStatus))
      ?.label ?? String(targetStatus)

  const labels = fields.map((field) =>
    t(field.labelKey, { defaultValue: field.field })
  )

  return (
    <Alert
      type={"warning"}
      showIcon
      role={"alert"}
      className={"mb-3"}
      data-testid={"lead-stage-retry"}
      data-target-status={String(targetStatus)}
      message={t("leadWorkspace.missing.banner.title", { stage })}
      description={
        labels.length
          ? t("leadWorkspace.missing.banner.description", {
              count: labels.length,
              fields: labels.join(", ")
            })
          : t("leadWorkspace.missing.banner.ready", { stage })
      }
      action={
        <Flex gap={"small"} align={"center"}>
          <Button
            size={"small"}
            type={"primary"}
            loading={saving}
            onClick={onRetry}
          >
            {t("leadWorkspace.missing.banner.retry", { stage })}
          </Button>
          <Button size={"small"} onClick={onDismiss}>
            {t("leadWorkspace.missing.banner.dismiss")}
          </Button>
        </Flex>
      }
    />
  )
}

export { LeadStageRetryBanner }
